import React from 'react';
import { formatDate } from '@/lib/utils';

export interface PrintDocumentTitleProps {
  title: string;
  subtitle?: string;
  docNo?: string;
  docNoLabel?: string;
  date?: string | Date;
  dateLabel?: string;
  extraMeta?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Standard Print Document Title Component (No QR Code, Symmetrically Centered)
 * Reusable across all A4 corporate / administrative print documents in APV ERP.
 */
export default function PrintDocumentTitle({
  title,
  subtitle,
  docNo,
  docNoLabel = 'Số / No.',
  date,
  dateLabel = 'Ngày',
  extraMeta,
  className = 'mb-4 pb-2 border-b-2 border-black',
  children,
}: PrintDocumentTitleProps) {
  const formattedDate = date
    ? typeof date === 'string'
      ? date
      : formatDate(date)
    : null;

  return (
    <div className={`text-center ${className}`}>
      <h1 className="text-[20px] font-bold uppercase tracking-wider text-black leading-tight">
        {title}
      </h1>
      {subtitle && (
        <h2 className="text-[12px] font-medium italic text-slate-600 mt-0.5">
          {subtitle}
        </h2>
      )}
      {(docNo || formattedDate || extraMeta) && (
        <div className="text-[11px] font-medium text-slate-700 mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5">
          {docNo && (
            <span>
              {docNoLabel}: <strong className="font-mono text-black">{docNo}</strong>
            </span>
          )}
          {docNo && formattedDate && <span>•</span>}
          {formattedDate && (
            <span>
              {dateLabel}: <span className="font-semibold text-black">{formattedDate}</span>
            </span>
          )}
          {extraMeta && (
            <>
              {(docNo || formattedDate) && <span>•</span>}
              <span>{extraMeta}</span>
            </>
          )}
        </div>
      )}
      {children}
    </div>
  );
}
